import { PalmettoButton, PalmettoIcon, PalmettoProgressBar, PalmettoTag } from '@palmetto/dsm-components';
import { Table } from '@palmetto/palmetto-components';
import { DateTime } from 'luxon';
import { useCallback } from 'react';

import { useGetDocumentPullReportsQuery, DocumentPullReport } from '../../services/documentPull';

const DEFAULT_LIMIT = 25;

const formatDate = (value: string): string => DateTime.fromISO(value).toFormat('M/d/yy h:mm a');

const formatCount = (value: number): string => value.toLocaleString('en-US');

const BYTE_UNITS = ['MB', 'GB', 'TB', 'PB'];
const BYTES_PER_MB = 1024 ** 2;

/**
 * Renders a byte count at a human scale, starting at MB and stepping up a unit per 1024.
 *
 * MB is the floor because these are document bundles: even a single-account pull is a fraction of a
 * megabyte, and a column mixing KB with GB is hard to compare down.
 */
const formatBytes = (bytes: number | undefined): string => {
  if (bytes === undefined || Number.isNaN(bytes)) return '—';

  const megabytes = bytes / BYTES_PER_MB;
  const exponent =
    megabytes < 1 ? 0 : Math.min(Math.floor(Math.log(megabytes) / Math.log(1024)), BYTE_UNITS.length - 1);
  const scaled = megabytes / 1024 ** exponent;
  // A decimal only where it carries information: 0.6 MB and 9.8 GB, but 512 MB rather than 512.0 MB.
  const rendered = scaled < 10 ? scaled.toFixed(1).replace(/\.0$/, '') : scaled.toFixed(0);

  return `${rendered} ${BYTE_UNITS[exponent]}`;
};

const formatLabel: Record<string, string> = {
  pdf: 'PDF',
  zip: 'ZIP',
};

const statusTagProps: Record<string, { color: 'grey' | 'error' | 'warning'; icon?: 'bolt' | 'warningTriangle' }> = {
  completed: { color: 'grey' },
  failed: { color: 'error', icon: 'bolt' },
  completedWithWarning: { color: 'warning', icon: 'warningTriangle' },
};

interface ActionsCellProps {
  row: DocumentPullReport;
  onDownload: (id: string) => void;
  onDownloadErrors: (id: string) => void;
}

const ActionsCell = ({ row, onDownload, onDownloadErrors }: ActionsCellProps) => {
  const isActionable = row.status === 'completed' || row.status === 'completedWithWarning';
  const hasIssues = row.errorCount > 0 || row.warningCount > 0;
  const handleClick = useCallback(() => onDownload(row.id), [onDownload, row.id]);
  const handleErrorsClick = useCallback(() => onDownloadErrors(row.id), [onDownloadErrors, row.id]);

  return (
    <div className="flex flex-row gap-2">
      {hasIssues && (
        <PalmettoButton
          emphasis="tertiary"
          size="xs"
          type="button"
          aria-label="Download error log"
          title="Download error log CSV"
          onClick={handleErrorsClick}
          style={{ color: 'var(--color-brand-warning-500)' }}
        >
          {/* A warning triangle rather than a second download arrow: sitting next to the report
              download, two identical icons separated only by shade were indistinguishable at a
              glance. Matches the warning tag this row already shows when there are issues. */}
          <PalmettoIcon name="warningTriangle" size="sm" />
        </PalmettoButton>
      )}
      <PalmettoButton
        emphasis="tertiary"
        size="xs"
        type="button"
        disabled={!isActionable}
        aria-label="Download report"
        title="Download report"
        onClick={handleClick}
      >
        <PalmettoIcon name="download" size="sm" />
      </PalmettoButton>
      <PalmettoButton emphasis="tertiary" size="xs" type="button" disabled={!isActionable} aria-label="Email">
        <PalmettoIcon name="mail" size="sm" />
      </PalmettoButton>
    </div>
  );
};

const renderStatus = (row: DocumentPullReport) => {
  const { status, progress, accountCount, successfulAccountCount, errorCount } = row;
  const tagConfig = statusTagProps[status];

  if (tagConfig) {
    const label = status === 'completedWithWarning' ? 'Completed' : status.charAt(0).toUpperCase() + status.slice(1);
    const showCounts = status === 'completedWithWarning' && successfulAccountCount !== undefined && accountCount > 0;
    const countTitle = showCounts
      ? `${successfulAccountCount!.toLocaleString()} / ${accountCount.toLocaleString()} accounts`
      : undefined;

    return (
      <span title={countTitle}>
        <PalmettoTag label={label} color={tagConfig.color} icon={tagConfig.icon} size="sm" />
      </span>
    );
  }

  if (status === 'pending' || status === 'running') {
    return (
      <div className="gap-1 flex flex-col items-center">
        <PalmettoProgressBar position={progress ?? 0} max={100} />
        <span className="text-xs font-bold">{progress ?? 0}%</span>
        {errorCount > 0 && (
          <span className="text-danger text-xs">
            {errorCount} error{errorCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>
    );
  }

  return <span className="text-sm">{status}</span>;
};

export const ReportsTab = () => {
  const { data, isLoading, isError } = useGetDocumentPullReportsQuery({ limit: DEFAULT_LIMIT, offset: 0 });

  const handleDownload = useCallback((id: string) => {
    window.location.href = `/api/document-pull/${id}/download`;
  }, []);

  const handleDownloadErrors = useCallback((id: string) => {
    window.location.href = `/api/document-pull/${id}/errors.csv`;
  }, []);

  const columns = [
    {
      heading: 'Report',
      dataKey: 'reportName',
      render: (_cell: any, row: any) => (
        <div className="flex flex-col">
          <span className="text-sm">{row.reportName || `Export ${row.id.slice(-8)}`}</span>
          {row.trancheName && (
            <span className="text-xs text-text-secondary">Generated from tranche: {row.trancheName}</span>
          )}
        </div>
      ),
    },
    {
      heading: 'Format',
      dataKey: 'format',
      render: (cell: any) => <span className="text-sm">{formatLabel[cell] ?? cell}</span>,
    },
    {
      heading: 'Creator',
      dataKey: 'creatorName',
      render: (cell: unknown, row: unknown) => (
        <span className="text-sm">{(cell as string) || (row as DocumentPullReport).creatorEmail || '—'}</span>
      ),
    },
    {
      heading: 'Created',
      dataKey: 'createdAt',
      render: (cell: any) => <span className="text-sm">{cell ? formatDate(cell) : '\u2014'}</span>,
    },
    {
      heading: 'Accounts',
      dataKey: 'accountCount',
      render: (_cell: unknown, row: unknown) => {
        const report = row as DocumentPullReport;
        // Once finished, show how many of the requested accounts actually produced documents — the
        // gap is the shortfall someone would otherwise have to open the error report to notice.
        const succeeded = report.successfulAccountCount;
        return (
          <span className="text-sm tabular-nums">
            {succeeded !== undefined && succeeded !== report.accountCount
              ? `${formatCount(succeeded)} / ${formatCount(report.accountCount)}`
              : formatCount(report.accountCount)}
          </span>
        );
      },
    },
    {
      heading: 'Size',
      dataKey: 'downloadSizeBytes',
      render: (cell: unknown) => (
        <span className="text-sm tabular-nums">{formatBytes(cell as number | undefined)}</span>
      ),
    },
    {
      heading: 'Status',
      dataKey: 'status',
      render: (_cell: any, row: any) => renderStatus(row),
    },
    // TODO: re-enable once delivery is in use
    // {
    //   heading: 'Delivered',
    //   dataKey: 'deliveredAt',
    //   render: (cell: any) => <span className="text-sm">{cell ? formatDate(cell) : '\u2014'}</span>,
    // },
    {
      heading: 'Actions',
      width: 140,
      render: (_cell: any, row: any) => (
        <ActionsCell row={row} onDownload={handleDownload} onDownloadErrors={handleDownloadErrors} />
      ),
    },
  ];

  if (isError) {
    return (
      <div className="rounded-md bg-secondary flex items-center justify-center p-8">
        <p className="text-secondary">Failed to load reports.</p>
      </div>
    );
  }

  const reports = data?.reports ?? [];
  const isEmpty = !isLoading && reports.length === 0;

  if (isEmpty) {
    return (
      <div className="rounded-md bg-secondary flex flex-col items-center gap-2 p-8">
        <p className="text-secondary text-lg font-bold">No reports yet</p>
        <p className="text-secondary text-sm">Generated reports will appear here.</p>
      </div>
    );
  }

  return <Table columns={columns} rows={reports} isLoading={isLoading} rowKey="id" isScrollable={{ x: true }} />;
};
